"use client";
import { useEffect, useRef, useState, FormEvent } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { Globe2, ArrowRight, CheckCircle2 } from "lucide-react";
import { api, useApp, apiUrl, errorText } from "./provider";
export function Challenge({ onToken }: { onToken: (s: string) => void }) {
  const { turnstile, locale } = useApp(),
    ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!turnstile) return;
    let widget: string | undefined;
    let cancelled = false;
    const render = () => {
      const ts = (window as any).turnstile;
      if (!cancelled && ref.current && ts)
        widget = ts.render(ref.current, {
          sitekey: turnstile,
          callback: onToken,
          "expired-callback": () => onToken(""),
          language: locale === "zh" ? "zh-cn" : "en",
        });
    };
    let script = document.querySelector<HTMLScriptElement>(
      "script[data-turnstile]",
    );
    if ((window as any).turnstile) render();
    else {
      if (!script) {
        script = document.createElement("script");
        script.src =
          "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.dataset.turnstile = "1";
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener("load", render);
    }
    return () => {
      cancelled = true;
      script?.removeEventListener("load", render);
      if (widget) (window as any).turnstile?.remove(widget);
    };
  }, [turnstile, locale, onToken]);
  return <div ref={ref} />;
}
export function AuthScreen() {
  const { t, error, message, refresh, providers, development, emailDelivery, user, ready } = useApp(),
    router = useRouter(),
    params = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") || "login"),
    [busy, setBusy] = useState(false),
    [done, setDone] = useState(false),
    [challenge, setChallenge] = useState(""),
    [phoneSent, setPhoneSent] = useState(false),
    [phone, setPhone] = useState("");
  const submitting = useRef(false);
  const [inlineError, setInlineError] = useState("");
  const [pending, setPending] = useState<{ email: string; purpose: "login" | "verify"; requestId?: string } | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const [challengeKey, setChallengeKey] = useState(0);
  useEffect(() => {
    if (!resendIn) return;
    const timer = setTimeout(() => setResendIn(value => Math.max(0, value - 1)), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);
  useEffect(() => {
    const requested = params.get("mode") || "login";
    setMode(
      ["login", "register", "forgot", "reset", "verify", "phone", "email"].includes(
        requested,
      )
        ? requested
        : "login",
    );
    setDone(false);
  }, [params]);
  useEffect(() => {
    if (params.get("error")) error(new Error(params.get("error")!));
  }, [params, error]);
  useEffect(() => {
    if (ready && user && !["reset", "verify", "email-code"].includes(mode) && !(mode === "email" && !user.verified)) {
      router.replace("/profile");
    }
  }, [ready, user, mode, router]);
  const labels: Record<string, [string, string]> = {
    login: ["Welcome back", "欢迎回来"],
    register: ["Join the community", "加入社区"],
    forgot: ["Reset your password", "重置密码"],
    reset: ["Choose a new password", "设置新密码"],
    verify: ["Verify your email", "验证您的邮箱"],
    phone: ["Sign in with your phone", "使用手机号登录"],
    email: ["Sign in with an email code", "使用邮箱验证码登录"],
    "email-code": ["Check your email", "请查看您的邮箱"],
  };
  function change(next: string) {
    if (submitting.current) return;
    setMode(next);
    setDone(false);
    setPhoneSent(false);
    setInlineError("");
    setChallenge("");
    setChallengeKey(value => value + 1);
    setPending(null);
  }
  function codeSent(email: string, purpose: "login" | "verify", requestId?: string) {
    setPending({ email, purpose, requestId });
    setMode("email-code");
    setResendIn(requestId ? 60 : 0);
    setChallenge("");
    setChallengeKey(value => value + 1);
  }
  async function resendCode() {
    if (!pending || submitting.current || resendIn > 0) return;
    submitting.current = true;
    setBusy(true);
    setInlineError("");
    try {
      const result = await api("auth/email-otp", { email: pending.email, purpose: pending.purpose, challenge });
      codeSent(pending.email, pending.purpose, result.requestId);
      message(t("A new code is on its way. Use the latest email.", "新验证码已发送，请查看最新邮件。"));
    } catch (e) {
      setInlineError(t(...errorText(e)));
    } finally {
      setChallenge("");
      setChallengeKey(value => value + 1);
      submitting.current = false;
      setBusy(false);
    }
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setInlineError("");
    setBusy(true);
    const values = Object.fromEntries(new FormData(event.currentTarget));
    try {
      if (mode === "email-code" && pending) {
        await api("auth/email-otp/verify", { ...pending, code: values.code });
        await refresh();
        message(t("Your email is verified. Welcome to KnowYourSign!", "邮箱验证成功，欢迎加入KnowYourSign！"));
        router.replace("/profile");
      } else if (mode === "email") {
        const email = String(values.email || "").trim().toLowerCase();
        const result = await api("auth/email-otp", { email, purpose: "login", challenge });
        codeSent(email, "login", result.requestId);
      } else if (mode === "verify") {
        await api("auth/verify", { token: params.get("token") });
        await refresh();
        setDone(true);
      } else if (mode === "reset") {
        await api("auth/reset", {
          token: params.get("token"),
          password: values.password,
        });
        setDone(true);
      } else if (mode === "phone") {
        await api("auth/phone", {
          ...values,
          phone: phone.replace(/[\s()-]/g, ""),
          challenge,
        });
        if (phoneSent) {
          await refresh();
          router.replace("/profile");
        } else {
          setPhoneSent(true);
          message(t("Your verification code is on its way.", "验证码已发送。"));
        }
      } else {
        const result = await api(`auth/${mode}`, {
          ...values,
          email: String(values.email || "").trim(),
          challenge,
        });
        if (mode === "forgot") {
          setDone(true);
        } else if (mode === "register") {
          codeSent(String(values.email).trim().toLowerCase(), "verify", result.requestId);
          await refresh();
          if (result.mailSent === false) setInlineError(t("Your account was created, but the email could not be sent. Use Resend code to try again.", "账户已创建，但邮件发送失败。请重新发送验证码。"));
        } else {
          await refresh();
          router.replace("/profile");
        }
      }
    } catch (e) {
      error(e);
      setInlineError(t(...errorText(e)));
    } finally {
      submitting.current = false;
      setBusy(false);
      setChallenge("");
      setChallengeKey(value => value + 1);
    }
  }
  return (
    <div className="auth-layout">
      <aside className="auth-story">
        <div>
          <div className="eyebrow">
            {t("A COMMUNITY WITHOUT BORDERS", "无国界的社区")}
          </div>
          <h1>
            {t("Your signs.", "您的手语。")}
            <br />
            {t("Our shared story.", "我们共同的故事。")}
          </h1>
          <p>
            {t(
              "Help preserve the signs that make your community unique. Every contribution adds a new perspective.",
              "帮助保存您社区独特的手语，每一次贡献都带来新的视角。",
            )}
          </p>
        </div>
        <Globe2 size={85} strokeWidth={0.7} />
        <p>
          {t(
            "Explore freely. Contribute thoughtfully. Grow together.",
            "自由探索，用心贡献，共同成长。",
          )}
        </p>
      </aside>
      <section className="auth-form">
        <h2>{t(...(labels[mode] || labels.login))}</h2>
        <p>
          {t(
            "A living dictionary starts with people like you.",
            "鲜活的词典，从您开始。",
          )}
        </p>
        {done ? (
          <div className="success-panel">
            <CheckCircle2 size={40} />
            <h3>
              {mode === "forgot"
                ? t("Check your inbox", "请查看收件箱")
                : t("You’re all set", "已完成")}
            </h3>
            <p>
              {mode === "forgot"
                ? t(
                    "If an account exists, you’ll receive a password reset link.",
                    "如果账户存在，您将收到密码重置链接。",
                  )
                : mode === "verify"
                  ? t(
                      "Your email is verified. You can now share and like signs.",
                      "您的邮箱已验证，现在可以分享手语和点赞。",
                    )
                  : t(
                      "Your password has been updated. Please sign in again.",
                      "您的密码已更新，请重新登录。",
                    )}
            </p>
            <Link
              className="button primary"
              href={mode === "verify" ? "/profile" : "/auth"}
              onClick={() => change("login")}
            >
              {t("Continue", "继续")}
              <ArrowRight size={17} />
            </Link>
          </div>
        ) : (
          <>
            {mode === "email-code" && pending && (
              <p className="notice" role="status">
                {pending.purpose === "login"
                  ? t("If an account exists, we’ll send a six-digit code to", "如果账户存在，六位验证码将发送至")
                  : t("Enter the six-digit verification code sent to", "请输入发送至以下邮箱的六位验证码")}{" "}
                <strong>{pending.email}</strong>. {t("Codes expire in 10 minutes.", "验证码10分钟后失效。")}
              </p>
            )}
            {["login", "email"].includes(mode) && (
              <div className="auth-methods" aria-label={t("Sign-in method", "登录方式")}>
                <button type="button" aria-pressed={mode === "login"} disabled={busy} onClick={() => change("login")}>{t("Password", "密码")}</button>
                <button type="button" aria-pressed={mode === "email"} disabled={busy || providers.email === false} onClick={() => change("email")}>{t("Email code", "邮箱验证码")}</button>
              </div>
            )}
            <form onSubmit={submit} aria-busy={busy} key={mode + (pending?.requestId || "")}>
              {inlineError && (
                <p className="auth-inline-error" role="alert">
                  {inlineError}
                </p>
              )}
              {mode === "register" && (
                <label>
                  {t("Display name", "显示名称")}
                  <input
                    name="username"
                    required
                    minLength={1}
                    maxLength={60}
                    autoComplete="nickname"
                  />
                </label>
              )}
              {["login", "register", "forgot", "email"].includes(mode) && (
                <label>
                  {t("Email address", "电子邮箱")}
                  <input
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    maxLength={254}
                    placeholder="you@example.com"
                  />
                </label>
              )}
              {mode === "email-code" && (
                <label>
                  {t("6-digit email code", "六位邮箱验证码")}
                  <input className="otp-input" name="code" required pattern="[0-9]{6}" inputMode="numeric" maxLength={6} minLength={6} autoComplete="one-time-code" autoFocus />
                </label>
              )}
              {["login", "register", "reset"].includes(mode) && (
                <label>
                  {t("Password", "密码")}
                  <input
                    type="password"
                    name="password"
                    required
                    minLength={mode === "login" ? 1 : 12}
                    maxLength={128}
                    autoComplete={
                      mode === "login" ? "current-password" : "new-password"
                    }
                  />
                  {mode !== "login" && (
                    <small>
                      {t("Use at least 12 characters.", "请使用至少12个字符。")}
                    </small>
                  )}
                </label>
              )}
              {mode === "phone" && (
                <>
                  <label>
                    {t(
                      "Phone number with country code",
                      "含国家代码的手机号码",
                    )}
                    <input
                      type="tel"
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      pattern="\+[1-9][0-9\s()-]{7,20}"
                      placeholder="+65 1234 5678"
                      autoComplete="tel"
                      readOnly={phoneSent}
                    />
                  </label>
                  {phoneSent && (
                    <label>
                      {t("6-digit verification code", "六位验证码")}
                      <input
                        name="code"
                        required
                        pattern="[0-9]{6}"
                        inputMode="numeric"
                        maxLength={6}
                        autoComplete="one-time-code"
                      />
                    </label>
                  )}
                </>
              )}
              {mode === "verify" && (
                <p className="notice">
                  {t(
                    "Confirm below to verify the email address associated with this link.",
                    "请点击下方按钮验证此链接关联的邮箱。",
                  )}
                </p>
              )}
              <label className="honeypot" aria-hidden="true">
                Website
                <input name="website" tabIndex={-1} autoComplete="off" />
              </label>
              {!["verify", "reset"].includes(mode) && (
                <Challenge key={challengeKey} onToken={setChallenge} />
              )}
              <button disabled={busy || (mode === "email-code" && !pending?.requestId)} className="button primary">
                {busy
                  ? t("Please wait…", "请稍候…")
                  : mode === "email"
                    ? t("Send sign-in code", "发送登录验证码")
                    : mode === "email-code"
                      ? t("Verify and continue", "验证并继续")
                  : mode === "register"
                    ? t("Create account", "创建账户")
                    : mode === "forgot"
                      ? t("Send reset link", "发送重置链接")
                      : mode === "verify"
                        ? t("Verify email", "验证邮箱")
                        : mode === "reset"
                          ? t("Save new password", "保存新密码")
                          : mode === "phone" && !phoneSent
                            ? t("Send verification code", "发送验证码")
                            : t("Sign in", "登录")}
                <ArrowRight size={17} />
              </button>
            </form>
            {mode === "email-code" && (
              <div className="auth-links">
                <button type="button" disabled={busy || resendIn > 0} onClick={resendCode}>
                  {resendIn > 0 ? t(`Resend code in ${resendIn}s`, `${resendIn}秒后重新发送`) : t("Resend code", "重新发送验证码")}
                </button>
              </div>
            )}
            {["login", "email"].includes(mode) && (
              <div className="auth-links">
                <button onClick={() => change("register")}>
                  {t("Create an account", "创建账户")}
                </button>
                <button onClick={() => change("forgot")}>
                  {t("Forgot password?", "忘记密码？")}
                </button>
              </div>
            )}
            {!["login", "email"].includes(mode) && (
              <div className="auth-links">
                <button onClick={() => change("login")}>
                  {t("Back to sign in", "返回登录")}
                </button>
              </div>
            )}
            {["login", "register", "email"].includes(mode) && (
              <>
                <div className="divider">
                  {t("or continue with", "或使用以下方式")}
                </div>
                <div className="oauth">
                  {["google", "github", "wechat"].map((provider) =>
                    providers[provider] ? (
                      <a
                        className="button"
                        href={apiUrl(`oauth/${provider}`)}
                        key={provider}
                      >
                        {provider === "google" ? "Google" : provider === "github" ? "GitHub" : t("WeChat", "微信")}
                        <ArrowRight size={14} />
                      </a>
                    ) : (
                      <button
                        className="button"
                        disabled
                        key={provider}
                        title={t("Provider not configured", "服务尚未配置")}
                      >
                        {provider === "google" ? "Google" : provider === "github" ? "GitHub" : t("WeChat", "微信")}{" "}
                        · {t("Not connected", "尚未连接")}
                      </button>
                    ),
                  )}
                </div>
                <button
                  className="text-button"
                  disabled={!providers.phone}
                  onClick={() => change("phone")}
                >
                  {t("Use a phone number", "使用手机号码")}
                  {!providers.phone &&
                    ` · ${t("Not connected yet", "尚未连接")}`}
                </button>
              </>
            )}
            {development && emailDelivery === "local" && (
              <p className="auth-footer">
                {t(
                  "Development email: codes and links are saved in the private local mail outbox.",
                  "开发环境：验证码和链接保存在私有的本地邮件目录中。",
                )}
              </p>
            )}
            <p className="auth-footer">
              {t(
                "By joining, you agree to our community guidelines and privacy practices.",
                "注册即表示您同意我们的社区准则和隐私规则。",
              )}{" "}
              <Link href="/about#guidelines">{t("Read more", "了解更多")}</Link>
            </p>
          </>
        )}
      </section>
    </div>
  );
}
